<template>
  <div class="h-full flex flex-col overflow-hidden text-sm select-none">

    <!-- Context bar: shows current file, branch, and selection status -->
    <div class="px-2 py-1.5 border-b border-gray-700 shrink-0 space-y-0.5">
      <div class="flex items-center gap-1.5 text-[11px] text-gray-400 min-w-0">
        <span class="shrink-0">📄</span>
        <span class="truncate">{{ editorStore.activeFile?.path ?? 'No file open' }}</span>
      </div>
      <div class="flex items-center gap-1.5 text-[11px] text-gray-400 min-w-0">
        <span class="shrink-0">⎇</span>
        <span class="truncate">{{ gitStore.status?.current ?? 'No branch' }}</span>
      </div>
      <div v-if="selectionHint" class="flex items-center gap-1.5 text-[11px] text-blue-400 min-w-0">
        <span class="shrink-0">✂</span>
        <span class="truncate">{{ selectionHint }} selected — will be sent as context</span>
      </div>
    </div>

    <!-- Quick-action buttons -->
    <div class="flex gap-1 p-2 border-b border-gray-700 shrink-0">
      <button
        v-for="action in ACTIONS"
        :key="action.label"
        class="flex-1 text-[11px] bg-gray-700 hover:bg-gray-600 active:bg-gray-500 rounded px-1.5 py-1 transition-colors"
        :title="action.prompt"
        @click="chatRef?.send(action.prompt)"
      >
        {{ action.label }}
      </button>
    </div>

    <!-- Chat panel -->
    <div class="flex-1 min-h-0">
      <ClaudeChat ref="chatRef" />
    </div>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import ClaudeChat     from './ClaudeChat.vue'
import { useEditorStore } from '@/stores/editorStore'
import { useGitStore }    from '@/stores/gitStore'

const editorStore = useEditorStore()
const gitStore    = useGitStore()

const chatRef = ref<InstanceType<typeof ClaudeChat>>()

// ── Quick actions ─────────────────────────────────────────────────────────────

const ACTIONS = [
  {
    label:  'Explain',
    prompt: 'Explain what the currently open file does. Focus on the overall purpose and any non-obvious logic.',
  },
  {
    label:  'Refactor',
    prompt: 'Refactor the currently open file for clarity, maintainability, and Craft CMS / PHP 8.3 best practices. Output the complete file using the <file> format.',
  },
  {
    label:  'Generate',
    prompt: 'Generate a unit test for the currently open file. Output the complete test file using the <file> format.',
  },
] as const

// ── Selection tracking ────────────────────────────────────────────────────────
// Monaco selection is not reactive — we subscribe to its change event.

const selectionText = ref('')

watch(
  () => editorStore.editorInstance,
  (editor, _prev, onCleanup) => {
    if (!editor) { selectionText.value = ''; return }
    const disposable = editor.onDidChangeCursorSelection(() => {
      const sel = editor.getSelection()
      selectionText.value = sel ? (editor.getModel()?.getValueInRange(sel) ?? '') : ''
    })
    onCleanup(() => disposable.dispose())
  },
  { immediate: true },
)

const selectionHint = computed<string>(() => {
  const len = selectionText.value.length
  if (len === 0) return ''
  const lines = selectionText.value.split('\n').length
  return lines > 1 ? `${lines} lines` : `${len} chars`
})
</script>
